
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import { EventStatus } from '@/services/eventService';
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { eventFormSchema, EventFormValues } from '../schemas/eventFormSchema';
import EventEditorTabContent from '../event-editor/EventEditorTabContent';
import { EventType } from '@/lib/types';
import { useEventSubmit } from '../event-editor/useEventSubmit';

interface EventCreatorModalProps {
  open: boolean;
  onClose: () => void;
  onEventCreated: () => void;
}

const EventCreatorModal = ({ open, onClose, onEventCreated }: EventCreatorModalProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const { toast } = useToast();

  // Default values for the form - moved to a function to ensure fresh defaults each time
  const getDefaultValues = () => ({
    eventName: "",
    description: "",
    startDate: new Date(),
    endDate: new Date(),
    startTime: new Date(),
    endTime: new Date(),
    city: "",
    venueName: "",
    address: "",
    region: "",
    isOnline: false,
    eventUrl: "https://",
    registrationUrl: "",
    eventType: "conference" as EventType,
    audience: [],
    creditPoints: "",
    maxAttendees: "",
    imageUrl: "",
    professions: [],
    timeNotSpecified: true,
    postAccredited: false,
    status: EventStatus.DRAFT,
  });

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: getDefaultValues(),
  });

  // Reset form when modal is opened
  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
      setIsFormDirty(false);
    }
  }, [open, form]);

  // Log form validation errors
  useEffect(() => {
    const subscription = form.watch(() => {
      setIsFormDirty(form.formState.isDirty);

      if (Object.keys(form.formState.errors).length > 0) {
        console.log("Creator form validation errors:", form.formState.errors);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const {
    isSubmitting,
    handleSubmit,
  } = useEventSubmit({ 
    eventId: null, // null because we're creating a new event
    onEventUpdated: onEventCreated, // Use the onEventCreated callback
    onClose: () => {
      form.reset();
      setIsFormDirty(false);
      onClose();
    }
  });

  const handleCloseAttempt = () => {
    if (isFormDirty) {
      setShowUnsavedChangesAlert(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedChangesAlert(false);
    form.reset();
    setIsFormDirty(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedChangesAlert(false);
  };

  const submitHandler = async (values: EventFormValues) => {
    console.log('EventCreatorModal submitting with values:', values);
    try {
      await handleSubmit(values);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(open) => !open && handleCloseAttempt()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-white pb-2 border-b">
            <DialogTitle>Új esemény létrehozása</DialogTitle>
            <DialogDescription>
              Töltse ki az esemény adatait, majd kattintson a Mentés gombra az esemény létrehozásához.
            </DialogDescription>
          </DialogHeader>

          <EventEditorTabContent
            form={form}
            isSubmitting={isSubmitting}
            onSubmit={submitHandler}
            onClose={handleCloseAttempt}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={showUnsavedChangesAlert} onOpenChange={setShowUnsavedChangesAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Nem mentett változtatások</AlertDialogTitle>
            <AlertDialogDescription>
              Nem mentette el az új eseményt. Biztosan szeretné bezárni az űrlapot? A nem mentett adatok elvesznek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Mégse</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>Bezárás mentés nélkül</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EventCreatorModal;
